import React, { useMemo, useState, forwardRef } from 'react';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import {
  FieldArrayDndFormik, FieldCustom, FieldImageGalleryUpload, FieldTextarea
} from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use'
import { useFormikForwardRef } from 'hooks'
import { linkRegex } from 'services/constants';

const ItemSchema = Yup.object().shape({
  text: Yup.string().required('Campo obrigatório'),
  name: Yup.string().required('Campo obrigatório'),
  about: Yup.string().required('Campo obrigatório'),
  cardUrl: Yup.string().matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
    .required('Campo obrigatório'),
  image: Yup.object({
    url: Yup.string(),
    alt: Yup.string(),
  }).required('Campo obrigatório'),
});

// eslint-disable-next-line react/prop-types
const ItemForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  return (
    <Formik
      ref={formRef}
      initialValues={initialValues}
      validationSchema={ItemSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          title="Nome"
          name="name"
          info="Primeiro e último nome"
          component={FieldCustom}
          maxLetters={120}
        />
        <Field
          title="Testemunho"
          name="text"
          component={FieldTextarea}
          maxLetters={255}
        />
        <Field
          title="Imagem"
          name="image"
          info="Imagem de retrato"
          component={FieldImageGalleryUpload}
        />
        <Field
          title="Subtítulo"
          name="about"
          info="Descrição da pessoa, ex: estudante, alumni, investigador..."
          component={FieldCustom}
        />
        <Field
          title="URL"
          name="cardUrl"
          info="Introduzir o url"
          component={FieldCustom}
        />
      </Form>
    </Formik>
  )
})

const TestimonialsWithLinkSchema = Yup.object().shape({
  data: Yup.array().of(Yup.object().shape({ text: Yup.string() }))
    .required('Campo obrigatório'),
});

const TestimonialsWithLinkEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps)

  const initialValues = useMemo(() => {
    const {
      data, title, isEditor
    } = initialProps;

    const dataComputed = data.map((item) => {
      const { person, text, cardUrl } = item
      const {
        name, image: url, about, alt
      } = person
      return {
        text,
        name,
        about,
        cardUrl,
        image: {
          alt,
          url,
        },
        isEditor
      }
    })

    return {
      title,
      data: dataComputed,
      isEditor
    }
  }, [initialProps]);

  const handleFormChange = newValues => {
    const { data } = newValues;
    const { data: dataRaw } = initialProps
    const moduleData = data.map((
      {
        name,
        text,
        about,
        cardUrl,
        image: { url, alt }
      },
      index,
    ) => ({
      ...dataRaw[index],
      text,
      cardUrl,
      person: {
        name,
        about,
        image: url,
        alt,
      }
    }))

    const newProps = { ...initialProps, ...newValues, data: moduleData };
    const isValid = TestimonialsWithLinkSchema.isValidSync(newValues)
    if (isValid) setEditorProps(newProps)
  };

  useDebounce(() => {
    onChange(editorProps);
  }, 200,
  [editorProps]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={TestimonialsWithLinkSchema}
      validate={handleFormChange}
    >
      <Form>
        <Field
          title="Título"
          name="title"
          info="Uma frase para secção"
          component={FieldCustom}
          maxLetters={120}
        />
        <Field
          name="data"
          component={FieldArrayDndFormik}
          columns={[
            {
              accessor: 'name',
              Header: 'Nome',

            },
            {
              accessor: 'text',
              Header: 'Descrição',

            }
          ]}
          rowKey="name"
          ItemForm={ItemForm}
        />
      </Form>
    </Formik>
  );
};
TestimonialsWithLinkEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    title: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      person: PropTypes.shape({
        image: PropTypes.string,
        alt: PropTypes.string,
        about: PropTypes.string,
        name: PropTypes.string,
      })
    }))
  }),
};

TestimonialsWithLinkEditor.defaultProps = {
  initialProps: {
    title: '',
    data: []
  },
};

export default TestimonialsWithLinkEditor;
